import { cva, type VariantProps } from "class-variance-authority";
import * as React from "react";
import { Link, type LinkProps } from "react-router-dom";

import { cn } from "@/lib/utils";
import styles from "./button.module.scss";
import { Spinner } from "./spinner";

const buttonVariants = cva(styles.button, {
  variants: {
    variant: {
      default: styles.variantDefault,
      destructive: styles.variantDestructive,
      outline: styles.variantOutline,
      secondary: styles.variantSecondary,
      success: styles.variantSuccess,
      ghost: styles.variantGhost,
      link: styles.variantLink,
      btnLink: styles.variantOutline,
    },
    size: {
      default: styles.sizeDefault,
      sm: styles.sizeSm,
      lg: styles.sizeLg,
      icon: styles.sizeIcon,
    },
  },
  defaultVariants: {
    variant: "default",
    size: "default",
  },
});

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
  text?: string;
  to?: LinkProps["to"];
}

const Button = React.forwardRef<
  HTMLButtonElement | HTMLAnchorElement,
  ButtonProps
>(
  (
    {
      className,
      variant,
      size,
      loading = false,
      disabled,
      text,
      children,
      to,
      onClick,
      ...props
    },
    ref,
  ) => {
    const isDisabled = !!(loading || disabled);
    const buttonClasses = cn(buttonVariants({ variant, size, className }));

    // If 'to' prop is provided, render as Link
    if (to) {
      return (
        <Link
          to={to}
          className={buttonClasses}
          onClick={(e) => {
            if (isDisabled) {
              e.preventDefault();
              return;
            }
            if (onClick) {
              // Convert Link's MouseEvent to Button's MouseEvent for onClick handler
              onClick(e as unknown as React.MouseEvent<HTMLButtonElement>);
            }
          }}
          style={{
            pointerEvents: isDisabled ? "none" : undefined,
            opacity: isDisabled ? 0.5 : undefined,
            textDecoration: "none",
          }}
          {...(props as Omit<
            React.AnchorHTMLAttributes<HTMLAnchorElement>,
            keyof LinkProps
          >)}
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
        >
          {loading && <Spinner />}
          {loading && text ? text : children}
        </Link>
      );
    }

    // Otherwise render as button
    return (
      <button
        className={buttonClasses}
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        disabled={isDisabled}
        onClick={onClick}
        {...props}
      >
        {loading && <Spinner />}
        {loading && text ? text : children}
      </button>
    );
  },
);
Button.displayName = "Button";

export { Button, buttonVariants };
